import { useEffect, useMemo, useState } from 'react'
import { api, ErroreApi } from '../../servizi/api.ts'
import { useAree, useSessione } from '../../servizi/sessione.tsx'
import { Avviso, Caselle } from '../../componenti/Pezzi.tsx'
import { Bottone, Campo, Etichetta, Modale, Scelta, Spunta } from '../../componenti/base.tsx'
import { SceltaComune, SceltaMultipla, useLingue } from '../../componenti/persona-campi.tsx'
import { codiceDaVoce, opzioniConAltreVoci } from '../../servizi/lingue.ts'
import type { ComuneScelto } from '../../servizi/regole-anagrafica.ts'
import { scaricaCsv } from '../../servizi/csv.ts'
import { Guscio, conteggio, usaElenco } from '../../componenti/elenco.tsx'
import { comuneLavoroLeggibile, type Mediatore } from '../../servizi/mediazioni.ts'
import { ProposteDoppioni, UnisciMediatori, type VoceUnione } from './UnisciMediatori.tsx'
import { ImportaMediatori } from './ImportaMediatori.tsx'
import { ConsegnaInvito, type InvitoConsegnato } from '../UtentiSistema.tsx'
import { opzioniSedi, vociSedi } from '../../servizi/sedi.ts'

/**
 * Albo dei mediatori. Chi gestisce l'albo (Direzione, amministrazione) aggiunge e modifica i
 * mediatori e li collega all'utenza con cui entrano nel gestionale: cosi' ricevono gli avvisi e
 * trovano le loro mediazioni. Si collegano solo utenze con il ruolo di mediatore.
 *
 * Dal 19/10/2026 l'albo si sfoltisce: un mediatore mai usato si elimina (il comando compare solo
 * su quelli), gli altri si disattivano; quelli importati dal vecchio sistema si disattivano anche
 * tutti insieme, dopo un'anteprima con i numeri. I disattivati restano fra i «Non più attivi».
 *
 * Dalla 465 i doppioni si uniscono: «Unisci i doppioni» propone i nomi che probabilmente sono la
 * stessa persona, «Unisci» nella riga parte da quel mediatore. Lo storico dei doppioni passa al
 * mediatore tenuto e i loro nomi compaiono sotto il suo. I «finti» si disattivano da «Modifica».
 *
 * Dal 22/10/2026 (469) «Disattiva» e «Riattiva» stanno nella riga: la Direzione non trovava la spunta
 * dentro «Modifica». La scheda porta anche anagrafica e contratto, che vede solo chi gestisce l'albo.
 *
 * Dal 27/10/2026 «Dai l'accesso», nella riga di chi non usa il gestionale, fa in un passo quello che
 * prima ne chiedeva tre in due pagine: utenza, incarico di Mediatore sull'area, collegamento. Dal
 * 30/10/2026 lo usa chiunque gestisca l'albo: chi non gestisce gli utenti crea solo utenze nuove, e
 * dalla riga rinnova l'invito di chi non e' ancora entrato («Nuovo invito»).
 *
 * Dal 28/10/2026 (477) un mediatore lavora in una o piu' sedi, oppure in tutte: colonna e filtro
 * nell'elenco, caselle in «Modifica». Le sedi non cambiano che cosa vede il mediatore: chi programma
 * una mediazione trova prima i mediatori della sede.
 *
 * Dal 30/10/2026 i filtri «Lingua» e «Paese di origine». Il paese e' un dato dell'anagrafica: il
 * filtro compare solo a chi gestisce l'albo, e l'API lo rifiuta agli altri. Lo stesso giorno (486)
 * «Scarica l'elenco» passa anche ai coordinatori, con il permesso `mediatore.esporta`, e chi gestisce
 * l'albo inserisce i mediatori nuovi anche da Excel («Importa da Excel», ImportaMediatori.tsx).
 *
 * Dal 2/11/2026 (532) ogni mediatore ha il comune in cui lavora, obbligatorio in «Aggiungi» e «Modifica»,
 * e facoltativamente il recapito dove riceve, che vede chi programma una mediazione dopo averla
 * programmata. I mediatori inseriti prima restano senza comune finche' qualcuno non li modifica: nella
 * riga «comune mancante», e il filtro «Comune» → «— non indicato —» li trova.
 */

type Utenza = { id: string; nome: string; cognome: string; email: string; mediatore_id: string | null }

/** Tipi di contratto (469); «Altro» chiede il testo. */
const TIPI_CONTRATTO = [
  { valore: 'dipendente', etichetta: 'Dipendente' },
  { valore: 'collaborazione', etichetta: 'Collaborazione (co.co.co.)' },
  { valore: 'occasionale', etichetta: 'Prestazione occasionale' },
  { valore: 'partita_iva', etichetta: 'Libero professionista (partita IVA)' },
  { valore: 'volontario', etichetta: 'Volontario' },
  { valore: 'altro', etichetta: 'Altro' },
]

const giorno = (data: string) => data.split('-').reverse().join('/')

/**
 * Riga di GET /mediatori/riepilogo. Le voci facoltative (anagrafica, contratto, attivita') arrivano
 * solo a chi gestisce l'albo (486).
 */
type VoceRiepilogo = {
  cognome: string; nome: string; interno: boolean; lingue: string[]; attivo: boolean
  usa_gestionale: boolean; dal_vecchio_archivio: boolean; altri_nomi: string[]
  tutte_le_sedi: boolean; sedi_albo: string[]
  comune_lavoro_nome: string | null; comune_lavoro_provincia: string | null
  contratto_tipo?: string | null; contratto_tipo_altro?: string | null; contratto_indeterminato?: boolean
  contratto_fine?: string | null; ultima_attivita?: string | null; attivita?: number; sedi?: string[]
  email?: string | null; numero_albo?: string | null
}

/** Colonna del foglio dell'albo; `riservata`: solo per chi gestisce l'albo. */
type ColonnaRiepilogo = { titolo: string; valore: (v: VoceRiepilogo) => string; riservata?: true }

/** Le sedi del mediatore in una riga: «Tutte le sedi», i nomi, oppure niente. */
const sediInBreve = (m: Pick<Mediatore, 'sedi' | 'tutte_le_sedi'>) =>
  m.tutte_le_sedi ? 'Tutte le sedi' : (m.sedi ?? []).map((s) => s.nome).join(', ')

const FILTRI = [
  { valore: 'true', etichetta: 'Attivi' },
  { valore: 'false', etichetta: 'Non più attivi' },
  { valore: 'tutti', etichetta: 'Tutti' },
]

/** Valore del filtro «Lingua» per chi non ha lingue nell'albo. */
const NESSUNA_LINGUA = 'nessuna'
/** Valore del filtro «Comune» per chi non ha il comune in cui lavora (532). */
const NESSUN_COMUNE = 'nessuno'

export function Mediatori() {
  const { puo, sedi, paesi } = useSessione()
  const gestisce = puo('mediatore.gestisci')
  // «Dai l'accesso»: per chi gestisce l'albo. Chi gestisce anche utenti e incarichi usa pure le utenze
  // che esistono gia'; gli altri (coordinatori, Project Manager) creano solo utenze nuove.
  const accessoCompleto = gestisce && puo('utente.gestisci') && puo('ruolo.assegna')
  // «Scarica l'elenco» (486): coordinatori e chi gestisce l'albo, non chi lo legge soltanto.
  const esporta = puo('mediatore.esporta')
  const lingue = useLingue()
  const [cerca, setCerca] = useState('')
  const [attivo, setAttivo] = useState('true')
  const [sede, setSede] = useState('')
  const [lingua, setLingua] = useState('')
  const [paese, setPaese] = useState('')
  const [comune, setComune] = useState('')
  /** Mediatori attivi senza il comune in cui lavora (532), per l'avviso a chi gestisce l'albo. */
  const [senzaComune, setSenzaComune] = useState<number | null>(null)
  const [modifica, setModifica] = useState<Mediatore | 'nuovo' | null>(null)
  const [eliminando, setEliminando] = useState<Mediatore | null>(null)
  const [disattivando, setDisattivando] = useState<Mediatore | null>(null)
  const [riattivando, setRiattivando] = useState<string | null>(null)
  const [erroreRiga, setErroreRiga] = useState<string | null>(null)
  const [scaricando, setScaricando] = useState(false)
  const [archivio, setArchivio] = useState(false)
  const [proposte, setProposte] = useState(false)
  const [unione, setUnione] = useState<{ iniziali: VoceUnione[]; tenere: string } | null>(null)
  const [esito, setEsito] = useState<string | null>(null)
  const [accesso, setAccesso] = useState<Mediatore | null>(null)
  const [invitando, setInvitando] = useState<string | null>(null)
  const [importa, setImporta] = useState(false)
  const [nuovoInvito, setNuovoInvito] = useState<{ mediatore: Mediatore; utenteId: string; invito: InvitoConsegnato } | null>(null)

  const percorso = useMemo(() => {
    const p = new URLSearchParams({ attivo })
    if (cerca.trim()) p.set('cerca', cerca.trim())
    if (sede) p.set('sede', sede)
    // «Non indicata»: chi non ha lingue nell'albo, per completarle (2/11/2026).
    if (lingua === NESSUNA_LINGUA) p.set('senza_lingue', '1')
    else if (lingua) p.set('lingua', lingua)
    if (gestisce && paese) p.set('paese', paese)
    if (comune === NESSUN_COMUNE) p.set('senza_comune', '1')
    return `/mediatori?${p}`
  }, [cerca, attivo, sede, lingua, paese, comune, gestisce])
  const { dati, errore, finestra, ricarica } = usaElenco<Mediatore>(percorso)
  // Quanti attivi non hanno il comune: si riconta a ogni modifica, cosi' l'avviso sparisce a lavoro finito.
  const contaSenzaComune = () => {
    if (!gestisce) return
    api.get<{ totale?: number | string }[]>('/mediatori?senza_comune=1&limite=1')
      .then((r) => setSenzaComune(Number(r[0]?.totale ?? 0)))
      .catch(() => setSenzaComune(null))
  }
  useEffect(contaSenzaComune, [gestisce])
  const fatto = (testo: string) => {
    setModifica(null); setEliminando(null); setDisattivando(null); setArchivio(false); setUnione(null)
    setAccesso(null); setErroreRiga(null); setEsito(testo); ricarica(); contaSenzaComune()
  }

  /**
   * L'albo in un foglio, da girare alle sedi perche' dicano chi non lavora piu' con CIDIS (richiesta
   * della Direzione del 23/10/2026). L'ultima colonna e' vuota: la compilano loro.
   *
   * Dal 30/10/2026 (486) lo scaricano anche i coordinatori. Chi non gestisce l'albo ha le colonne che
   * vede a schermo: le altre l'API non gliele manda. L'ultima colonna, vuota, resta per tutti.
   */
  async function scaricaElenco() {
    setEsito(null); setErroreRiga(null); setScaricando(true)
    try {
      const voci = await api.get<VoceRiepilogo[]>('/mediatori/riepilogo')
      const tipo = (v: VoceRiepilogo) => v.contratto_tipo === 'altro' ? v.contratto_tipo_altro ?? 'Altro'
        : TIPI_CONTRATTO.find((t) => t.valore === v.contratto_tipo)?.etichetta ?? ''
      const tutte: ColonnaRiepilogo[] = [
        { titolo: 'Cognome', valore: (v) => v.cognome },
        { titolo: 'Nome', valore: (v) => v.nome },
        { titolo: 'N. albo CIDIS', valore: (v) => v.numero_albo ?? '', riservata: true },
        { titolo: 'Email', valore: (v) => v.email ?? '', riservata: true },
        { titolo: 'Stato', valore: (v) => v.attivo ? 'attivo' : 'non più attivo' },
        { titolo: 'Rapporto', valore: (v) => v.interno ? 'interno' : 'esterno' },
        { titolo: 'Lingue', valore: (v) => v.lingue.map(nomeLingua).join(', ') },
        { titolo: 'Sedi', valore: (v) => v.tutte_le_sedi ? 'tutte' : v.sedi_albo.join(', ') },
        { titolo: 'Comune in cui lavora', valore: (v) => comuneLavoroLeggibile(v) },
        { titolo: 'Sedi in cui ha lavorato', valore: (v) => (v.sedi ?? []).join(', '), riservata: true },
        { titolo: 'Ultima attività', valore: (v) => v.ultima_attivita ? giorno(v.ultima_attivita) : '', riservata: true },
        { titolo: 'Attività registrate', valore: (v) => String(v.attivita ?? ''), riservata: true },
        { titolo: 'Usa il gestionale', valore: (v) => v.usa_gestionale ? 'sì' : 'no' },
        { titolo: 'Dal vecchio archivio', valore: (v) => v.dal_vecchio_archivio ? 'sì' : 'no' },
        { titolo: 'Altri nomi', valore: (v) => v.altri_nomi.join(', ') },
        { titolo: 'Contratto', valore: tipo, riservata: true },
        { titolo: 'Fine del contratto', riservata: true,
          valore: (v) => v.contratto_indeterminato ? 'indeterminato' : v.contratto_fine ? giorno(v.contratto_fine) : '' },
        { titolo: 'Lavora ancora con CIDIS? (sì/no)', valore: () => '' },
      ]
      const colonne = tutte.filter((c) => gestisce || !c.riservata)
      scaricaCsv(`mediatori-${new Date().toISOString().slice(0, 10)}.csv`,
        colonne.map((c) => c.titolo), voci.map((v) => colonne.map((c) => c.valore(v))))
    } catch (e) {
      setErroreRiga(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine. Riprova fra poco.')
    } finally {
      setScaricando(false)
    }
  }

  /** Nuovo invito per chi ha l'utenza ma non e' ancora entrato: codice e collegamento da consegnare. */
  async function rinnovaInvito(m: Mediatore) {
    setEsito(null); setErroreRiga(null); setInvitando(m.id)
    try {
      const r = await api.post<InvitoConsegnato & { utente_id: string }>(`/mediatori/${m.id}/invito`, {})
      setNuovoInvito({ mediatore: m, utenteId: r.utente_id, invito: r })
    } catch (e) {
      setErroreRiga(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine. Riprova fra poco.')
    } finally {
      setInvitando(null)
    }
  }

  /** Riattivare non ha controlli da spiegare: torna subito fra gli attivi. */
  async function riattiva(m: Mediatore) {
    setEsito(null); setErroreRiga(null); setRiattivando(m.id)
    try {
      await api.patch(`/mediatori/${m.id}`, { attivo: true })
      fatto(`${m.nome} ${m.cognome} è di nuovo fra i mediatori attivi.`)
    } catch (e) {
      setErroreRiga(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine. Riprova fra poco.')
    } finally {
      setRiattivando(null)
    }
  }
  const nomeLingua = (codice: string) => lingue.find((l) => l.codice === codice.trim())?.nome ?? codice

  return (
    <>
      {esito && <Avviso tipo="successo">{esito}</Avviso>}
      {erroreRiga && <Avviso tipo="errore">{erroreRiga}</Avviso>}
      {gestisce && !!senzaComune && comune !== NESSUN_COMUNE && (
        <Avviso tipo="attenzione">
          {senzaComune === 1 ? 'Un mediatore attivo non ha' : `${senzaComune} mediatori attivi non hanno`} il comune in
          cui {senzaComune === 1 ? 'lavora' : 'lavorano'}: ora è obbligatorio. {senzaComune === 1
            ? 'Si può ancora scegliere per le mediazioni; completalo da «Modifica».'
            : 'Si possono ancora scegliere per le mediazioni; completali da «Modifica».'}{' '}
          <Bottone variante="discreto" dimensione="piccolo"
                   onClick={() => { setComune(NESSUN_COMUNE); setAttivo('true') }}>
            Mostrali
          </Bottone>
        </Avviso>
      )}
      <Guscio titolo="Mediatori" errore={errore} dati={dati} finestra={finestra}
              vuoto={cerca ? 'Nessun mediatore con questo nome.'
                : lingua || paese || comune ? 'Nessun mediatore con questi filtri.'
                : sede ? 'Nessun mediatore in questa sede.' : 'Nessun mediatore in elenco.'}
              sottotitolo={conteggio(finestra.totale, 'mediatore', 'mediatori')}
              azioni={<>
                <div className="filtro-intestazione">
                  <Campo tipo="search" etichetta="Cerca per nome" valore={cerca} cambia={setCerca} maxLength={120} />
                </div>
                <div className="filtro-intestazione">
                  <Scelta etichetta="Quali" valore={attivo} opzioni={FILTRI} cambia={setAttivo} />
                </div>
                <div className="filtro-intestazione">
                  <Scelta etichetta="Sede" valore={sede} vuota="Tutte" opzioni={opzioniSedi(sedi)} cambia={setSede} />
                </div>
                <div className="filtro-intestazione">
                  <Scelta etichetta="Lingua" valore={lingua} vuota="Tutte" cambia={(v) => setLingua(codiceDaVoce(v))}
                          opzioni={[{ valore: NESSUNA_LINGUA, etichetta: '— non indicata —' },
                            ...opzioniConAltreVoci(lingue)]} />
                </div>
                <div className="filtro-intestazione">
                  <Scelta etichetta="Comune" valore={comune} vuota="Tutti" cambia={setComune}
                          opzioni={[{ valore: NESSUN_COMUNE, etichetta: '— non indicato —' }]} />
                </div>
                {gestisce && (
                  <div className="filtro-intestazione">
                    <Scelta etichetta="Paese di origine" valore={paese} vuota="Tutti" cambia={setPaese}
                            opzioni={paesi.map((p) => ({ valore: p.codice_iso3, etichetta: p.nome }))} />
                  </div>
                )}
                {(gestisce || esporta) && (
                  <div className="mediazioni-comando-in-basso riga">
                    {gestisce && (
                      <Bottone variante="secondario" onClick={() => { setEsito(null); setProposte(true) }}>
                        Unisci i doppioni
                      </Bottone>
                    )}
                    {esporta && (
                      <Bottone variante="secondario" inCorso={scaricando} onClick={() => void scaricaElenco()}>
                        Scarica l’elenco
                      </Bottone>
                    )}
                    {gestisce && (
                      <>
                        <Bottone variante="secondario" onClick={() => { setEsito(null); setArchivio(true) }}>
                          Disattiva quelli del vecchio archivio
                        </Bottone>
                        <Bottone variante="secondario" onClick={() => { setEsito(null); setImporta(true) }}>
                          Importa da Excel
                        </Bottone>
                        <Bottone variante="primario" onClick={() => { setEsito(null); setModifica('nuovo') }}>
                          Aggiungi un mediatore
                        </Bottone>
                      </>
                    )}
                  </div>
                )}
              </>}
              intestazioni={['Nome', 'Rapporto', 'Comune', 'Lingue', 'Sedi', 'Gestionale', ...(gestisce ? [''] : [])]}>
        {dati?.map((m) => (
          <tr key={m.id}>
            <td data-etichetta="Nome" className="voce-principale">
              {m.cognome} {m.nome} {!m.attivo && <Etichetta>non più attivo</Etichetta>}
              {m.numero_albo && <div className="piccolo tenue">Albo CIDIS n. {m.numero_albo}</div>}
              {m.dal_vecchio_archivio && <div className="piccolo tenue">Dal vecchio archivio</div>}
              {!!m.altri_nomi?.length && <div className="piccolo tenue">Anche: {m.altri_nomi.join(', ')}</div>}
              {m.contratto_finito && m.contratto_fine && (
                <div className="piccolo tenue">Contratto finito il {giorno(m.contratto_fine)}</div>
              )}
            </td>
            <td data-etichetta="Rapporto"><Etichetta>{m.interno ? 'interno' : 'esterno'}</Etichetta></td>
            <td data-etichetta="Comune" className="piccolo">
              {comuneLavoroLeggibile(m) || <Etichetta tono="attenzione">comune mancante</Etichetta>}
            </td>
            <td data-etichetta="Lingue" className="piccolo">{m.lingue.map(nomeLingua).join(', ') || '—'}</td>
            <td data-etichetta="Sedi" className="piccolo">{sediInBreve(m) || <span className="tenue">—</span>}</td>
            <td data-etichetta="Gestionale" className="piccolo">
              {!m.usa_gestionale ? <span className="tenue">Non lo usa</span>
                : m.invito_in_attesa ? 'Invitato, non ancora entrato' : 'Usa il gestionale'}
            </td>
            {gestisce && (
              <td data-etichetta="">
                <div className="riga riga-a-destra">
                  {m.attivo && !m.usa_gestionale && (
                    <Bottone variante="secondario" dimensione="piccolo" aria-label={`Dai l’accesso a ${m.cognome} ${m.nome}`}
                             onClick={() => { setEsito(null); setErroreRiga(null); setAccesso(m) }}>
                      Dai l’accesso
                    </Bottone>
                  )}
                  {m.usa_gestionale && m.invito_in_attesa && (
                    <Bottone variante="secondario" dimensione="piccolo" aria-label={`Nuovo invito per ${m.cognome} ${m.nome}`}
                             inCorso={invitando === m.id} onClick={() => void rinnovaInvito(m)}>
                      Nuovo invito
                    </Bottone>
                  )}
                  <Bottone variante="discreto" dimensione="piccolo" onClick={() => { setEsito(null); setModifica(m) }}>
                    Modifica
                  </Bottone>
                  <Bottone variante="discreto" dimensione="piccolo"
                           onClick={() => { setEsito(null); setUnione({ iniziali: [m], tenere: m.id }) }}>
                    Unisci
                  </Bottone>
                  {m.attivo
                    ? (
                      <Bottone variante="discreto" dimensione="piccolo" aria-label={`Disattiva ${m.cognome} ${m.nome}`}
                               onClick={() => { setEsito(null); setErroreRiga(null); setDisattivando(m) }}>
                        Disattiva
                      </Bottone>
                    )
                    : (
                      <Bottone variante="discreto" dimensione="piccolo" aria-label={`Riattiva ${m.cognome} ${m.nome}`}
                               inCorso={riattivando === m.id} onClick={() => void riattiva(m)}>
                        Riattiva
                      </Bottone>
                    )}
                  {m.eliminabile && (
                    <Bottone variante="discreto" dimensione="piccolo" onClick={() => { setEsito(null); setEliminando(m) }}>
                      Elimina
                    </Bottone>
                  )}
                </div>
              </td>
            )}
          </tr>
        ))}
      </Guscio>

      {modifica && (
        <ModuloMediatore mediatore={modifica === 'nuovo' ? null : modifica} chiudi={() => setModifica(null)}
                         fatto={fatto} />
      )}
      {eliminando && <EliminaMediatore mediatore={eliminando} chiudi={() => setEliminando(null)} fatto={fatto} />}
      {disattivando && <DisattivaMediatore mediatore={disattivando} chiudi={() => setDisattivando(null)} fatto={fatto} />}
      {archivio && <DisattivaArchivio chiudi={() => setArchivio(false)} fatto={fatto} />}
      {importa && <ImportaMediatori chiudi={() => setImporta(false)} importati={ricarica} />}
      {accesso && (
        <DaiAccesso mediatore={accesso} completo={accessoCompleto} chiudi={() => setAccesso(null)} fatto={fatto} />
      )}
      {nuovoInvito && (
        <ConsegnaInvito utente={{ id: nuovoInvito.utenteId, nome: nuovoInvito.mediatore.nome, cognome: nuovoInvito.mediatore.cognome }}
                        invito={nuovoInvito.invito}
                        titolo={`Nuovo invito per ${nuovoInvito.mediatore.nome} ${nuovoInvito.mediatore.cognome}`}
                        nota="Il collegamento e il codice di prima non valgono più. Al primo accesso si sceglie la password."
                        chiudi={() => {
                          setNuovoInvito(null)
                          fatto(`Il nuovo invito di ${nuovoInvito.mediatore.nome} ${nuovoInvito.mediatore.cognome} è pronto.`)
                        }} />
      )}
      {proposte && (
        <ProposteDoppioni chiudi={() => setProposte(false)}
                          rivedi={(g) => { setProposte(false); setUnione({ iniziali: g.mediatori, tenere: g.proposto }) }} />
      )}
      {unione && (
        <UnisciMediatori iniziali={unione.iniziali} tenere={unione.tenere} chiudi={() => setUnione(null)} fatto={fatto} />
      )}
    </>
  )
}

/**
 * Elimina un mediatore mai usato. Se nel frattempo e' stato scelto per una mediazione, l'API
 * risponde con il motivo e propone di disattivarlo.
 */
function EliminaMediatore({ mediatore, chiudi, fatto }: {
  mediatore: Mediatore; chiudi: () => void; fatto: (testo: string) => void
}) {
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const nome = `${mediatore.nome} ${mediatore.cognome}`

  async function elimina() {
    setErrore(null); setInCorso(true)
    try {
      await api.elimina(`/mediatori/${mediatore.id}`)
      fatto(`${nome} non è più nell’albo.`)
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine. Riprova fra poco.')
    } finally {
      setInCorso(false)
    }
  }

  return (
    <Modale titolo={`Eliminare ${nome}?`} chiudi={chiudi} fuocoIniziale="azioni"
            azioni={<>
              <Bottone variante="discreto" onClick={chiudi}>Lascia stare</Bottone>
              <Bottone variante="critico" inCorso={inCorso} onClick={() => void elimina()}>Elimina</Bottone>
            </>}>
      <p>
        Non compare in nessuna mediazione, intervento o servizio: si toglie dall’albo del tutto e non si
        recupera. Se serve di nuovo, si aggiunge da capo.
      </p>
      {mediatore.usa_gestionale && (
        <p>È collegato a un’utenza: l’utenza resta, ma non avrà più mediazioni da vedere.</p>
      )}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </Modale>
  )
}

/**
 * Disattiva un mediatore (469, comando nella riga). Con mediazioni in programma l'API rifiuta e
 * dice che cosa fare prima.
 */
function DisattivaMediatore({ mediatore, chiudi, fatto }: {
  mediatore: Mediatore; chiudi: () => void; fatto: (testo: string) => void
}) {
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const nome = `${mediatore.nome} ${mediatore.cognome}`

  async function disattiva() {
    setErrore(null); setInCorso(true)
    try {
      await api.patch(`/mediatori/${mediatore.id}`, { attivo: false })
      fatto(`${nome} è fra i «Non più attivi».`)
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine. Riprova fra poco.')
    } finally {
      setInCorso(false)
    }
  }

  return (
    <Modale titolo={`Disattivare ${nome}?`} chiudi={chiudi} fuocoIniziale="azioni"
            azioni={<>
              <Bottone variante="discreto" onClick={chiudi}>Lascia stare</Bottone>
              <Bottone variante="critico" inCorso={inCorso} onClick={() => void disattiva()}>Disattiva</Bottone>
            </>}>
      <p>
        Non si potrà più scegliere per le nuove mediazioni. Resta nell’albo fra i «Non più attivi», con
        le mediazioni e i servizi che ha fatto, e si riattiva quando serve.
      </p>
      {mediatore.usa_gestionale && (
        <p>L’utenza collegata resta: per toglierle l’accesso usa «Disattiva» in Amministrazione → Utenti.</p>
      )}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </Modale>
  )
}

type Anteprima = {
  da_disattivare: number; con_utenza: number; con_mediazioni: number; gia_disattivati: number; importati: number
}

const quanti = (n: number, uno: string, tanti: string) => (n === 1 ? `1 ${uno}` : `${n} ${tanti}`)

/**
 * Disattivazione in blocco dei mediatori importati dal vecchio sistema: prima i numeri, poi la
 * conferma con il numero visto. Se nel frattempo e' cambiato, l'API non tocca niente e la finestra
 * mostra quello nuovo.
 */
function DisattivaArchivio({ chiudi, fatto }: { chiudi: () => void; fatto: (testo: string) => void }) {
  const [anteprima, setAnteprima] = useState<Anteprima | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  const leggi = () => api.get<Anteprima>('/mediatori/vecchio-archivio')
    .then(setAnteprima)
    .catch((e) => setErrore(e instanceof ErroreApi ? e.message : 'Non riesco a contare i mediatori. Riprova fra poco.'))
  useEffect(() => { void leggi() }, [])

  async function disattiva() {
    if (!anteprima) return
    setErrore(null); setInCorso(true)
    try {
      const r = await api.post<{ disattivati: number }>('/mediatori/vecchio-archivio/disattiva',
        { attesi: anteprima.da_disattivare })
      fatto(`Fatto: ${quanti(r.disattivati, 'mediatore disattivato', 'mediatori disattivati')}. `
        + 'Li trovi fra i «Non più attivi» e ognuno si riattiva da «Modifica».')
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine. Riprova fra poco.')
      if (e instanceof ErroreApi && e.codice === 'ANTEPRIMA_CAMBIATA') void leggi()
    } finally {
      setInCorso(false)
    }
  }

  const n = anteprima?.da_disattivare ?? 0
  return (
    <Modale titolo="Disattiva i mediatori del vecchio archivio" chiudi={chiudi} fuocoIniziale="azioni"
            azioni={<>
              <Bottone variante="discreto" onClick={chiudi}>{anteprima && n === 0 ? 'Chiudi' : 'Lascia stare'}</Bottone>
              {anteprima && n > 0 && (
                <Bottone variante="critico" inCorso={inCorso} onClick={() => void disattiva()}>
                  Disattiva {quanti(n, 'mediatore', 'mediatori')}
                </Bottone>
              )}
            </>}>
      {!anteprima && !errore && <p className="tenue">Conto i mediatori…</p>}
      {anteprima && (
        n > 0
          ? (
            <>
              <p>
                <strong>{quanti(n, 'mediatore importato dal vecchio sistema è ancora attivo',
                  'mediatori importati dal vecchio sistema sono ancora attivi')}</strong> e{' '}
                {n === 1 ? 'si può disattivare' : 'si possono disattivare'}.
                Dopo non {n === 1 ? 'si potrà' : 'si potranno'} più scegliere per le nuove mediazioni; mediazioni,
                interventi e servizi già registrati restano come sono.
              </p>
              <p className="piccolo">
                {n === 1 ? 'Resta' : 'Restano'} fra i «Non più attivi» e ognuno si riattiva da «Modifica».
              </p>
            </>
          )
          : <p>Non c’è nessun mediatore del vecchio archivio da disattivare.</p>
      )}
      {anteprima && (anteprima.con_utenza > 0 || anteprima.con_mediazioni > 0) && (
        <p className="piccolo tenue">
          Restano attivi:{' '}
          {[
            anteprima.con_utenza > 0 && quanti(anteprima.con_utenza, 'collegato a un’utenza', 'collegati a un’utenza'),
            anteprima.con_mediazioni > 0
              && quanti(anteprima.con_mediazioni, 'con mediazioni in programma', 'con mediazioni in programma'),
          ].filter(Boolean).join('; ')}.
        </p>
      )}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </Modale>
  )
}

/**
 * Sedi del mediatore (477): «Lavora in tutte le sedi», oppure le singole sedi, oppure nessuna. Una
 * sede chiusa gia' collegata resta fra le caselle finche' non la si toglie.
 */
function SediMediatore({ tutte, scelte, collegate, cambiaTutte, cambiaScelte }: {
  tutte: boolean
  scelte: string[]
  collegate: { id: string; nome: string; attiva: boolean }[]
  cambiaTutte: (tutte: boolean) => void
  cambiaScelte: (scelte: string[]) => void
}) {
  const { sedi } = useSessione()
  const voci = [...vociSedi(sedi),
    ...collegate.filter((c) => !c.attiva).map((c) => ({ codice: c.id, nome: `${c.nome} (chiusa)` }))]
  return (
    <fieldset className="gruppo-caselle">
      <legend>Sedi CIDIS in cui lavora</legend>
      <Spunta etichetta="Lavora in tutte le sedi" valore={tutte} cambia={cambiaTutte} />
      {!tutte && (voci.length
        ? <Caselle voci={voci} scelte={scelte} cambia={cambiaScelte} />
        : <p className="piccolo tenue">Nessuna sede CIDIS attiva.</p>)}
      <p className="nota-modulo">
        Facoltative. Chi programma una mediazione in una sede trova prima i mediatori di quella sede e quelli
        di tutte le sedi. Non cambiano quello che il mediatore vede: solo le mediazioni affidate a lui.
      </p>
    </fieldset>
  )
}

function ModuloMediatore({ mediatore, chiudi, fatto }: {
  mediatore: Mediatore | null; chiudi: () => void; fatto: (testo: string) => void
}) {
  const lingue = useLingue()
  const [nome, setNome] = useState(mediatore?.nome ?? '')
  const [cognome, setCognome] = useState(mediatore?.cognome ?? '')
  const [interno, setInterno] = useState(mediatore?.interno ? 'si' : 'no')
  const [scelte, setScelte] = useState<string[]>(mediatore?.lingue.map((l) => l.trim()) ?? [])
  const [utente, setUtente] = useState(mediatore?.utente_id ?? '')
  const [attivo, setAttivo] = useState(mediatore?.attivo ?? true)
  const [tutteSedi, setTutteSedi] = useState(mediatore?.tutte_le_sedi ?? false)
  const [sediScelte, setSediScelte] = useState<string[]>(mediatore?.sedi?.map((s) => s.id) ?? [])
  const { paesi } = useSessione()
  const [comune, setComune] = useState<ComuneScelto>({
    codice: mediatore?.comune_residenza ?? '', testo: mediatore?.comune_residenza_nome ?? '',
    provincia: mediatore?.comune_residenza_provincia ?? '',
  })
  // Comune in cui lavora (532): obbligatorio, anche per salvare una modifica di chi non l'aveva.
  const [comuneLavoro, setComuneLavoro] = useState<ComuneScelto>({
    codice: mediatore?.comune_lavoro ?? '', testo: mediatore?.comune_lavoro_nome ?? '',
    provincia: mediatore?.comune_lavoro_provincia ?? '',
  })
  const [recapito, setRecapito] = useState(mediatore?.recapito ?? '')
  const [paese, setPaese] = useState(mediatore?.paese_origine ?? '')
  const [email, setEmail] = useState(mediatore?.email ?? '')
  const [numeroAlbo, setNumeroAlbo] = useState(mediatore?.numero_albo ?? '')
  const [erroreCampo, setErroreCampo] = useState<Record<string, string>>({})
  const [tipo, setTipo] = useState(mediatore?.contratto_tipo ?? '')
  const [altro, setAltro] = useState(mediatore?.contratto_tipo_altro ?? '')
  const [inizio, setInizio] = useState(mediatore?.contratto_inizio ?? '')
  const [fine, setFine] = useState(mediatore?.contratto_fine ?? '')
  const [indeterminato, setIndeterminato] = useState(mediatore?.contratto_indeterminato ?? false)
  const [certificato, setCertificato] = useState(
    mediatore?.certificato === true ? 'si' : mediatore?.certificato === false ? 'no' : '')
  const [utenze, setUtenze] = useState<Utenza[]>([])
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  useEffect(() => {
    api.get<Utenza[]>('/mediatori/utenze').then(setUtenze).catch(() => setUtenze([]))
  }, [])

  // Un'utenza gia' collegata a un altro mediatore non si propone.
  const collegabili = utenze.filter((u) => !u.mediatore_id || u.mediatore_id === mediatore?.id)

  const mancano = [
    ...(!nome.trim() ? ['nome'] : []), ...(!cognome.trim() ? ['cognome'] : []),
    ...(!comuneLavoro.codice ? [comuneLavoro.testo.trim() ? 'il comune in cui lavora scelto fra i suggerimenti'
      : 'il comune in cui lavora'] : []),
    ...(comune.testo.trim() && !comune.codice ? ['il comune di residenza scelto fra i suggerimenti'] : []),
    ...(email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ? ['un’email valida'] : []),
    ...(tipo === 'altro' && altro.trim().length < 2 ? ['quale contratto'] : []),
    ...(!indeterminato && inizio && fine && fine < inizio ? ['una fine del contratto dopo l’inizio'] : []),
  ]

  async function salva() {
    setErrore(null); setInCorso(true)
    const corpo = {
      nome: nome.trim(), cognome: cognome.trim(), interno: interno === 'si', lingue: scelte,
      utente_id: utente || null, ...(mediatore ? { attivo } : {}),
      tutte_le_sedi: tutteSedi, sedi: tutteSedi ? [] : sediScelte,
      comune_lavoro: comuneLavoro.codice || null,
      recapito: recapito.trim() || null,
      comune_residenza: comune.codice || null,
      paese_origine: paese || null,
      email: email.trim() || null,
      numero_albo: numeroAlbo.trim() || null,
      contratto_tipo: tipo || null,
      contratto_tipo_altro: tipo === 'altro' ? altro.trim() || null : null,
      contratto_inizio: inizio || null,
      contratto_fine: indeterminato ? null : fine || null,
      contratto_indeterminato: indeterminato,
      certificato: certificato === '' ? null : certificato === 'si',
    }
    try {
      if (mediatore) {
        await api.patch(`/mediatori/${mediatore.id}`, corpo)
        fatto(`${corpo.nome} ${corpo.cognome}: modifiche salvate.`)
      } else {
        await api.post('/mediatori', corpo)
        fatto(`${corpo.nome} ${corpo.cognome} è nell’albo${corpo.utente_id ? ' e riceverà gli avvisi delle sue mediazioni' : ''}.`)
      }
    } catch (e) {
      if (e instanceof ErroreApi && e.codice === 'NUMERO_ALBO_PRESENTE') setErroreCampo({ numero_albo: e.message })
      else setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine. Riprova fra poco.')
    } finally {
      setInCorso(false)
    }
  }

  return (
    <Modale larga titolo={mediatore ? `${mediatore.nome} ${mediatore.cognome}` : 'Aggiungi un mediatore'} chiudi={chiudi}
            azioni={<>
              <Bottone variante="discreto" onClick={chiudi}>Lascia stare</Bottone>
              <Bottone variante="primario" inCorso={inCorso} disabled={mancano.length > 0}
                       onClick={() => void salva()}>
                {mediatore ? 'Salva' : 'Aggiungi'}
              </Bottone>
            </>}>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <div className="griglia-modulo">
        <Campo etichetta="Nome" obbligatorio valore={nome} cambia={setNome} maxLength={100} />
        <Campo etichetta="Cognome" obbligatorio valore={cognome} cambia={setCognome} maxLength={100} />
        <Scelta etichetta="Rapporto con CIDIS" valore={interno} cambia={setInterno}
                opzioni={[{ valore: 'no', etichetta: 'Esterno' }, { valore: 'si', etichetta: 'Interno' }]} />
      </div>
      <div className="griglia-modulo">
        <SceltaComune etichetta="Comune in cui lavora" obbligatorio valore={comuneLavoro} cambia={setComuneLavoro}
                      aiuto="Dove è disponibile, non dove abita. Lo vede chi legge l’albo e chi programma una mediazione." />
        <Campo etichetta="Recapito" valore={recapito} cambia={setRecapito} maxLength={300}
               segnaposto="Via, numero civico, comune"
               aiuto="Facoltativo. Dove riceve le persone: lo vede chi programma una mediazione con lui, dopo averla programmata. Non va negli avvisi né nelle email." />
      </div>
      <SceltaMultipla etichetta="Lingue" voci={lingue} scelte={scelte} cambia={setScelte} vuota="— scegli una lingua —"
                      massimo={30} aiuto="Le lingue in cui fa mediazione: servono a trovarlo quando si programma." />
      <SediMediatore tutte={tutteSedi} scelte={sediScelte} collegate={mediatore?.sedi ?? []}
                     cambiaTutte={setTutteSedi} cambiaScelte={setSediScelte} />
      <div className="griglia-modulo">
        <Scelta etichetta="Utenza nel gestionale" valore={utente} cambia={setUtente}
                vuota="— nessuna: non usa il gestionale —"
                opzioni={collegabili.map((u) => ({ valore: u.id, etichetta: `${u.cognome} ${u.nome} (${u.email})` }))}
                aiuto={collegabili.length
                  ? 'Collegata, riceve gli avvisi e vede le sue mediazioni. Qui compaiono le utenze con il ruolo di mediatore.'
                  : 'Nessuna utenza con il ruolo di mediatore da collegare: prima assegna il ruolo dalla pagina degli utenti.'} />
      </div>
      {mediatore && (
        <Spunta etichetta="Attivo: si può scegliere per le nuove mediazioni" valore={attivo} cambia={setAttivo} />
      )}

      <h3 className="operatori-titoletto">Anagrafica e contratto</h3>
      <p className="nota-modulo">Facoltativi. Li vede solo chi gestisce l’albo.</p>
      <div className="griglia-modulo">
        <Campo etichetta="N. albo CIDIS" maxLength={30} valore={numeroAlbo} errore={erroreCampo.numero_albo}
               cambia={(v) => { setNumeroAlbo(v); setErroreCampo({}) }} aiuto="Il numero di iscrizione all’albo dei mediatori di CIDIS." />
        <Campo etichetta="Email" tipo="email" maxLength={200} valore={email} cambia={setEmail}
               aiuto="Per contattarlo. Non è l’email con cui entra nel gestionale." />
      </div>
      <div className="griglia-modulo">
        <SceltaComune etichetta="Comune di residenza" valore={comune} cambia={setComune}
                      aiuto="Dove abita. Non è il comune in cui lavora: questo lo vede solo chi gestisce l’albo." />
        <Scelta etichetta="Paese di origine" valore={paese} cambia={setPaese} vuota="— non indicato —"
                opzioni={paesi.map((p) => ({ valore: p.codice_iso3, etichetta: p.nome }))} />
        <Scelta etichetta="Certificazione di mediatore" valore={certificato} cambia={setCertificato}
                vuota="— non indicata —" opzioni={[{ valore: 'si', etichetta: 'Sì' }, { valore: 'no', etichetta: 'No' }]} />
      </div>
      <div className="griglia-modulo">
        <Scelta etichetta="Tipo di contratto" valore={tipo} cambia={setTipo} vuota="— non indicato —"
                opzioni={TIPI_CONTRATTO} />
        {tipo === 'altro' && (
          <Campo etichetta="Quale contratto" obbligatorio valore={altro} cambia={setAltro} maxLength={100} />
        )}
        <Campo tipo="date" etichetta="Inizio del contratto" valore={inizio} cambia={setInizio} />
        <Campo tipo="date" etichetta="Fine del contratto" valore={indeterminato ? '' : fine} cambia={setFine}
               disabilitato={indeterminato}
               aiuto="Quando finisce, chi gestisce l’albo riceve un avviso; il mediatore resta attivo finché non lo disattivi." />
      </div>
      <Spunta etichetta="Tempo indeterminato: il contratto non ha una fine" valore={indeterminato} cambia={setIndeterminato} />
      {mancano.length > 0 && (nome.trim() || cognome.trim() || mediatore) && (
        <p className="nota-modulo">Mancano ancora: {mancano.join(', ')}.</p>
      )}
    </Modale>
  )
}

/** Risposta di POST /mediatori/:id/accesso. */
type EsitoAccesso = {
  utente_id: string; utenza: 'nuova' | 'vecchio_archivio' | 'esistente'; incarico: 'assegnato' | 'gia_presente'
  invito: InvitoConsegnato | null
}

/** Un anno da oggi: la durata massima dell'utenza di un esterno e dell'incarico di Mediatore. */
const traUnAnno = () => new Date(Date.now() + 365 * 86_400_000).toISOString().slice(0, 10)

/**
 * «Dai l'accesso» (richiesta della Direzione del 27/10/2026): utenza, incarico di Mediatore sull'area
 * e collegamento della voce dell'albo, in un passo. Con l'email di un utente gia' in uso (una collega
 * che fa anche la mediatrice) si usa la sua utenza e le si aggiunge l'incarico; con una nuova si crea
 * l'utenza e l'invito da consegnare.
 */
function DaiAccesso({ mediatore, completo, chiudi, fatto }: {
  mediatore: Mediatore
  /** Gestisce anche utenti e incarichi: puo' usare l'utenza di un collega. Altrimenti solo utenze nuove. */
  completo: boolean
  chiudi: () => void; fatto: (testo: string) => void
}) {
  const { sedi } = useSessione()
  const { aree } = useAree()
  const nome = `${mediatore.nome} ${mediatore.cognome}`
  // L'email dell'albo (478), se c'e', e' la proposta: si cambia se entra con un altro indirizzo.
  const [email, setEmail] = useState(mediatore.email ?? '')
  const [area, setArea] = useState(() => aree.some((a) => a.codice === 'MIS') ? 'MIS' : aree[0]?.codice ?? '')
  // Le sedi dell'utenza: si propongono quelle dell'albo ancora aperte (477), oppure l'unica sede.
  const [sediUtenza, setSediUtenza] = useState<string[]>(() => {
    const dellAlbo = (mediatore.sedi ?? []).filter((s) => sedi.some((a) => a.id === s.id)).map((s) => s.id)
    return dellAlbo.length ? dellAlbo : sedi.length === 1 ? [sedi[0].id] : []
  })
  const [principale, setPrincipale] = useState(() => (sediUtenza.length === 1 ? sediUtenza[0] : ''))
  const cambiaSedi = (nuove: string[]) => {
    setSediUtenza(nuove)
    setPrincipale((p) => (nuove.includes(p) ? p : nuove.length === 1 ? nuove[0] : ''))
  }
  // Per un esterno l'utenza scade: la fine del contratto se arriva prima di un anno, altrimenti un anno.
  const [scadenza, setScadenza] = useState(() => {
    const fine = mediatore.contratto_fine
    return fine && fine > new Date().toISOString().slice(0, 10) && fine < traUnAnno() ? fine : traUnAnno()
  })
  const [errori, setErrori] = useState<Record<string, string>>({})
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const [consegna, setConsegna] = useState<{ utenteId: string; invito: InvitoConsegnato } | null>(null)

  async function dai() {
    const e: Record<string, string> = {}
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) e.email = 'Indirizzo email non valido.'
    if (!area) e.area = 'Scegli l’area.'
    setErrori(e); setErrore(null)
    if (Object.keys(e).length) return
    setInCorso(true)
    try {
      const r = await api.post<EsitoAccesso>(`/mediatori/${mediatore.id}/accesso`, {
        email: email.trim(), area, sedi: sediUtenza, sede_principale: principale || null,
        scadenza_account: mediatore.interno ? null : scadenza || null,
      })
      if (r.invito) {
        setConsegna({ utenteId: r.utente_id, invito: r.invito })
        return
      }
      fatto(r.incarico === 'assegnato'
        ? `L’utenza di ${nome} è collegata all’albo e ha ora anche l’incarico di Mediatore: le mediazioni affidate si vedono al prossimo accesso.`
        : `L’utenza di ${nome}, che aveva già l’incarico di Mediatore, è collegata all’albo.`)
    } catch (err) {
      const codice = err instanceof ErroreApi ? err.codice : null
      const campo = codice === 'SEDE_OBBLIGATORIA' || codice === 'SEDE_INESISTENTE' || codice === 'SEDE_NON_ATTIVA' ? 'sede'
        : codice === 'SEDE_PRINCIPALE_MANCANTE' || codice === 'SEDE_PRINCIPALE_NON_COLLEGATA' ? 'principale'
        : codice === 'SCADENZA_ACCOUNT' ? 'scadenza'
        : codice === 'AREA_FUORI_PERIMETRO' || codice === 'AMBITO_INESISTENTE' ? 'area'
        : codice === 'EMAIL_SEGNAPOSTO' || codice === 'UTENZA_NON_ATTIVA' || codice === 'UTENZA_GIA_COLLEGATA'
          || codice === 'UTENZA_TECNICA' || codice === 'SOLO_UTENZA_NUOVA' ? 'email' : null
      const testo = err instanceof ErroreApi ? err.message : 'Non è andata a buon fine. Riprova fra poco.'
      if (campo) setErrori({ [campo]: testo })
      else setErrore(testo)
    } finally {
      setInCorso(false)
    }
  }

  if (consegna) {
    return (
      <ConsegnaInvito utente={{ id: consegna.utenteId, nome: mediatore.nome, cognome: mediatore.cognome }}
                      invito={consegna.invito} titolo={`Accesso di ${nome} · consegna l’invito`}
                      nota="Al primo accesso si sceglie la password; le mediazioni affidate sono nel menu «Le mie mediazioni». Un invito scaduto si rinnova dalla riga del mediatore, «Nuovo invito»."
                      chiudi={() => fatto(`L’utenza di ${nome} è pronta e collegata all’albo: si entra con l’invito consegnato.`)} />
    )
  }

  return (
    <Modale titolo={`Dare l’accesso al gestionale a ${nome}?`} chiudi={chiudi} fuocoIniziale="corpo"
            azioni={<>
              <Bottone variante="discreto" onClick={chiudi}>Lascia stare</Bottone>
              <Bottone variante="primario" inCorso={inCorso} onClick={() => void dai()}>Dai l’accesso</Bottone>
            </>}>
      <p>
        In un passo: l’utenza con cui entra, l’incarico di Mediatore sull’area e il collegamento a questa voce
        dell’albo. Da quel momento riceve gli avvisi e vede le mediazioni che gli affidi.
      </p>
      <div className="griglia-modulo">
        <Campo etichetta="Email" tipo="email" obbligatorio maxLength={200} valore={email} cambia={setEmail}
               errore={errori.email}
               aiuto={completo
                 ? 'Se è l’indirizzo di un utente che usa già il gestionale (per esempio una collega che fa anche la mediatrice), si usa la sua utenza.'
                 : 'Un indirizzo che il gestionale non conosce ancora: da qui si crea un’utenza nuova. Per collegare un’utenza che c’è già serve chi gestisce gli utenti.'} />
        <Scelta etichetta="Area dell’incarico" obbligatorio valore={area} cambia={setArea} errore={errori.area}
                opzioni={aree.map((a) => ({ valore: a.codice, etichetta: a.nome }))} />
        {!mediatore.interno && (
          <Campo etichetta="Scadenza dell’utenza" tipo="date" valore={scadenza} cambia={setScadenza}
                 min={new Date().toISOString().slice(0, 10)} max={traUnAnno()} errore={errori.scadenza}
                 aiuto="È esterno: l’utenza dura al massimo un anno. Proposta: la fine del contratto, se viene prima." />
        )}
      </div>
      <fieldset className={`gruppo-caselle${errori.sede ? ' campo-errato' : ''}`}>
        <legend>Sedi CIDIS dell’utenza</legend>
        <Caselle voci={vociSedi(sedi)} scelte={sediUtenza} cambia={cambiaSedi} />
        {errori.sede && <p className="campo-errore" role="alert">{errori.sede}</p>}
        <p className="nota-modulo">
          Servono se non ha ancora un’utenza nel gestionale.{' '}
          {mediatore.tutte_le_sedi
            ? 'Nell’albo lavora in tutte le sedi: scegli qui quelle della sua utenza.'
            : 'Sono proposte le sedi dell’albo.'}
        </p>
      </fieldset>
      {sediUtenza.length > 1 && (
        <div className="griglia-modulo">
          <Scelta etichetta="Sede principale" obbligatorio vuota="— scegli —" valore={principale} cambia={setPrincipale}
                  errore={errori.principale}
                  opzioni={opzioniSedi(sedi).filter((o) => sediUtenza.includes(o.valore))} />
        </div>
      )}
      <p className="nota-modulo">
        L’incarico di Mediatore dura al massimo un anno: alla scadenza si proroga da Amministrazione → Utenti.
        Gli altri incarichi della persona restano com’erano.
      </p>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </Modale>
  )
}
